import { isEqual } from 'lodash';
import mapboxgl from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { Dimension1, Tab } from '@revelio/data-access';

import { useFetchTooltipData } from '../../../../../../shared/hooks/useFetchTooltipData';
import { useAllSelectionLists } from '../../../../filters/hooks/useAllSelectionLists';
import { useEffectiveFilters } from '../../../../filters/hooks/useEffectiveFilters';
import {
  FilterId,
  type PrimaryFilterId,
} from '../../../../filters/types/filters';
import { flattenMultiFiltersForTooltip } from '../../../../filters/utils/flattenMultiFiltersForTooltip';
import { getCompositionV2Filters } from '../../../../filters/utils/getCompositionV2Filters';
import { getDim1 } from '../../../../filters/utils/getDim1';
import { getPostingFilters } from '../../../../filters/utils/getPostingFilters';
import type { PopupContentProps } from '../utils/overview-map-tooltip-content';
import { useDebounce } from './useDebounce';
import { useHoveredFeature } from './useHoveredFeature';

type TooltipFeature = {
  id: number;
  type: number;
  label?: unknown;
  location?: unknown;
} | null;

interface UseOverviewTooltipOptions {
  enabled?: boolean;
  layerIds?: string[];
  buildFeature?: (
    props: Record<string, unknown>,
    layerId: string
  ) => Record<string, unknown> | TooltipFeature | null;
  metric?: string;
}

const TOOLTIP_DEBOUNCE_MS = 150;
const DEFAULT_TOOLTIP_WIDTH = 200;
const DEFAULT_TOOLTIP_HEIGHT = 180;
const MARGIN = 20;
const EMPTY_COMPOSITION_ENTITIES: [] = [];

export function useOverviewTooltip(
  primaryDimension: PrimaryFilterId = FilterId.GEOGRAPHY,
  options: UseOverviewTooltipOptions = {}
) {
  const { enabled = true, layerIds, buildFeature, metric } = options;

  const mapRef = useRef<mapboxgl.Map | null>(null);
  const measuredSizeRef = useRef({
    width: DEFAULT_TOOLTIP_WIDTH,
    height: DEFAULT_TOOLTIP_HEIGHT,
  });
  const rafIdRef = useRef<number | null>(null);
  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    content: PopupContentProps | null;
    x: number;
    y: number;
    anchor:
      | 'left'
      | 'right'
      | 'top'
      | 'bottom'
      | 'top-left'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-right';
  }>({
    isOpen: false,
    content: null,
    x: 0,
    y: 0,
    anchor: 'bottom',
  });

  const [isInteracting, setIsInteracting] = useState(false);
  const lastContentRef = useRef<PopupContentProps | null>(null);
  const lastPointerMoveAtRef = useRef(0);
  const lastInteractionEndAtRef = useRef(0);
  const currentLngLatRef = useRef<mapboxgl.LngLat | null>(null);

  const computeTooltipPosition = useCallback(
    (
      map: mapboxgl.Map,
      lngLat: mapboxgl.LngLat,
      size: { width: number; height: number }
    ) => {
      const point = map.project(lngLat);
      const container = map.getContainer();
      const rect = container.getBoundingClientRect();
      const { clientWidth: mapWidth } = container;

      const nearTop = point.y < size.height + MARGIN;
      const nearLeft = point.x < size.width / 2 + MARGIN;
      const nearRight = point.x > mapWidth - (size.width / 2 + MARGIN);

      const x = rect.left + point.x;
      const y = rect.top + point.y;
      let anchor:
        | 'left'
        | 'right'
        | 'top'
        | 'bottom'
        | 'top-left'
        | 'top-right'
        | 'bottom-left'
        | 'bottom-right' = 'bottom';

      if (nearTop && nearLeft) {
        anchor = 'top-left';
      } else if (nearTop && nearRight) {
        anchor = 'top-right';
      } else if (nearTop) {
        anchor = 'top';
      } else if (nearLeft) {
        anchor = 'bottom-left';
      } else if (nearRight) {
        anchor = 'bottom-right';
      }

      return { x, y, anchor };
    },
    []
  );

  const updateTooltipPosition = useCallback(() => {
    if (!mapRef.current || !currentLngLatRef.current) return;
    const position = computeTooltipPosition(
      mapRef.current,
      currentLngLatRef.current,
      measuredSizeRef.current
    );
    setTooltipState((prev) =>
      prev.x === position.x &&
      prev.y === position.y &&
      prev.anchor === position.anchor
        ? prev
        : {
            ...prev,
            ...position,
          }
    );
  }, [computeTooltipPosition]);

  const handleTooltipResize = useCallback(
    (size: { width: number; height: number }) => {
      const widthChanged =
        Math.abs(size.width - measuredSizeRef.current.width) > 2;
      const heightChanged =
        Math.abs(size.height - measuredSizeRef.current.height) > 2;
      if (!widthChanged && !heightChanged) return;
      measuredSizeRef.current = size;
      if (tooltipState.isOpen) {
        updateTooltipPosition();
      }
    },
    [tooltipState.isOpen, updateTooltipPosition]
  );

  const targetLayers = useMemo(
    () => (layerIds?.length ? layerIds : []),
    [layerIds]
  );
  const { hover, attach } = useHoveredFeature(mapRef, targetLayers, {
    buildFeature: (props, layerId) =>
      buildFeature ? buildFeature(props, layerId) : null,
    throttleMs: 1,
  });

  const [debouncedFeature, setDebouncedFeature] =
    useState<TooltipFeature>(null);
  const { run: setFeatureDebounced, cancel: cancelDebounce } = useDebounce(
    (f: TooltipFeature) => setDebouncedFeature(f),
    TOOLTIP_DEBOUNCE_MS
  );

  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  // TODO: remove this once the rest of the dashboard support multi-filters
  const tooltipFilters = useMemo(
    () => flattenMultiFiltersForTooltip(effectiveFilters),
    [effectiveFilters]
  );

  const postingEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'week',
  });
  const postingTooltipFilters = useMemo(
    () => flattenMultiFiltersForTooltip(postingEffectiveFilters),
    [postingEffectiveFilters]
  );
  const selectionLists = useAllSelectionLists();

  // For td_users metric, use homogenization to expand role/RICS selections
  // to the most granular level (required by tooltip APIs)
  const shouldHomogenize = metric === 'td_users';

  const selectionListsForHomogenization = useMemo(
    () => (shouldHomogenize ? selectionLists : undefined),
    [selectionLists, shouldHomogenize]
  );

  const compositionData = useMemo(
    () =>
      getCompositionV2Filters({
        primaryDimension,
        effectiveFilters: tooltipFilters,
        homogenize: shouldHomogenize,
        selectionListsForHomogenization,
      }),
    [
      primaryDimension,
      tooltipFilters,
      shouldHomogenize,
      selectionListsForHomogenization,
    ]
  );

  const postingFiltersBase = useMemo(
    () =>
      getPostingFilters(primaryDimension, postingTooltipFilters, {
        isSnapshot: true,
        homogenize: shouldHomogenize,
        selectionListsForHomogenization,
      }),
    [
      primaryDimension,
      postingTooltipFilters,
      shouldHomogenize,
      selectionListsForHomogenization,
    ]
  );

  const compositionEntities = useMemo(() => {
    if (metric === 'td_users') {
      return EMPTY_COMPOSITION_ENTITIES; // Entities come from tooltipFeature for map tooltips
    }
    return Array.isArray(compositionData.entities)
      ? compositionData.entities
      : EMPTY_COMPOSITION_ENTITIES;
  }, [compositionData.entities, metric]);

  const tooltipDimension = useMemo(
    () => getDim1(primaryDimension) ?? Dimension1.Geography,
    [primaryDimension]
  );

  // Keep the snapshot dates `getPostingFilters` derived from the page's date
  // filter (the selected snapshot, or a range's latest point, expanded to the
  // 2-week window postings need) so the tooltip reflects the selected date
  // rather than always the latest available week.
  const postingFilters = useMemo(
    () => ({
      ...postingFiltersBase,
      provider: [0],
      metric_mode: 'expected_hires',
    }),
    [postingFiltersBase]
  );

  const { loggedInUser } = useGetLoggedInUser();
  const userTabs = loggedInUser?.tabs || [];
  const hasPayAccess = userTabs.includes(Tab.Compensation);
  const hasPostingsAccess = userTabs.includes(Tab.Posting);

  const tooltipData = useFetchTooltipData({
    dim1: tooltipDimension,
    tooltipFeature: debouncedFeature,
    compositionEntities,
    compositionFilters: compositionData.filters,
    postingFilters,
    skipPosting: !hasPostingsAccess,
    skipCompensation: !hasPayAccess,
  });

  useEffect(() => {
    if (!enabled) return;
    if (!mapRef.current) return;

    if (isInteracting) {
      setTooltipState((prev) => ({ ...prev, isOpen: false }));
      cancelDebounce();
      setDebouncedFeature(null);
      lastContentRef.current = null;
      currentLngLatRef.current = null;
      return;
    }

    if (!hover.feature || !hover.coords) {
      setTooltipState((prev) => ({ ...prev, isOpen: false }));
      cancelDebounce();
      setDebouncedFeature(null);
      lastContentRef.current = null;
      currentLngLatRef.current = null;
      return;
    }

    const label = hover.feature?.label;
    const location = hover.feature?.location;
    const header = (() => {
      if (typeof label === 'string') return label;
      if (typeof location === 'string') return location;
      if (typeof label === 'number') return String(label);
      if (typeof location === 'number') return String(location);
      return '';
    })();
    const allowReappearPostInteraction =
      !debouncedFeature &&
      (lastInteractionEndAtRef.current === 0 ||
        lastPointerMoveAtRef.current > lastInteractionEndAtRef.current);

    const lngLat = new mapboxgl.LngLat(hover.coords[0], hover.coords[1]);
    currentLngLatRef.current = lngLat;

    if (hover.changed || allowReappearPostInteraction) {
      const showMapTooltip = metric === 'td_users';
      const skeletonContent: PopupContentProps = {
        header,
        headcount: '-',
        growthRate: '-',
        postings: '-',
        timeToFill: '-',
        salaryDist: ['', '', ''],
        marketTightness: '-',
        isLoading: true,
        showMarketTightness: showMapTooltip,
      };

      const position = computeTooltipPosition(
        mapRef.current,
        lngLat,
        measuredSizeRef.current
      );
      setTooltipState({
        isOpen: true,
        content: skeletonContent,
        ...position,
      });
      setFeatureDebounced(hover.feature);
    } else {
      updateTooltipPosition();
    }
  }, [
    hover,
    enabled,
    cancelDebounce,
    setFeatureDebounced,
    isInteracting,
    debouncedFeature,
    metric,
    computeTooltipPosition,
    updateTooltipPosition,
  ]);

  const content = useMemo(() => {
    if (!tooltipData) return null;
    const showMapTooltip = metric === 'td_users';
    return {
      ...tooltipData,
      showMarketTightness: showMapTooltip,
    };
  }, [tooltipData, metric]);

  useEffect(() => {
    if (!enabled) return;
    if (!content) return;
    const featureKey = (f: TooltipFeature): string | null =>
      f ? `${f.id}:${f.type}` : null;
    const currentKey = featureKey(hover.feature);
    const requestedKey = featureKey(debouncedFeature);
    if (currentKey && requestedKey && currentKey === requestedKey) {
      // TODO: look into better way to handle this
      const contentChanged = !isEqual(lastContentRef.current, content);
      if (contentChanged) {
        lastContentRef.current = content;
        setTooltipState((prev) => ({ ...prev, content }));
      }
    }
  }, [content, hover.feature, debouncedFeature, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!mapRef.current) return;
    if (!currentLngLatRef.current) return;
    if (!tooltipState.isOpen) return;

    const map = mapRef.current;
    const scheduleUpdate = () => {
      if (rafIdRef.current !== null) return;
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        updateTooltipPosition();
      });
    };

    map.on('move', updateTooltipPosition);
    map.on('zoom', updateTooltipPosition);
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);

    return () => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      map.off('move', updateTooltipPosition);
      map.off('zoom', updateTooltipPosition);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
    };
  }, [enabled, tooltipState.isOpen, updateTooltipPosition]);

  const initializeTooltipHandlers = useCallback(
    (map: mapboxgl.Map) => {
      if (!enabled) return;
      mapRef.current = map;
      const cleanup = attach(map);

      const handleZoomStart = () => setIsInteracting(true);
      const handleZoomEnd = () => {
        setIsInteracting(false);
        lastInteractionEndAtRef.current = performance.now();
      };
      const handleMoveStart = () => setIsInteracting(true);
      const handleMoveEnd = () => {
        setIsInteracting(false);
        lastInteractionEndAtRef.current = performance.now();
      };
      const handlePointerMove = () => {
        lastPointerMoveAtRef.current = performance.now();
      };

      map.on('zoomstart', handleZoomStart);
      map.on('zoomend', handleZoomEnd);
      map.on('movestart', handleMoveStart);
      map.on('moveend', handleMoveEnd);
      map.on('mousemove', handlePointerMove);

      return () => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
        map.off('zoomstart', handleZoomStart);
        map.off('zoomend', handleZoomEnd);
        map.off('movestart', handleMoveStart);
        map.off('moveend', handleMoveEnd);
        map.off('mousemove', handlePointerMove);
      };
    },
    [attach, enabled]
  );

  return {
    feature: debouncedFeature,
    initializeTooltipHandlers,
    tooltipState,
    handleTooltipResize,
  };
}
